/**
 * Draw charting thumbnail cards for the "All" visual overview (PRD.169).
 *
 * Renders one card per saved drawing record (newest first).
 * The preview is generated client-side from the structured drawing data
 * using an offscreen Konva Stage — no Firebase Storage thumbnail is stored.
 *
 * Each card matches the standard image thumbnail sizing used in the grid
 * and is visually distinguishable from raw images via a "Drawing" badge.
 *
 * Clicking opens the focused Draw workspace for that record.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import Konva from 'konva';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qEncounterVisualCharts,
  qPatientFilesByEncounter,
  qChartingTemplates,
  type EncounterVisualChartRow,
  type PatientFileRow,
  type ChartingTemplateRow,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import type { DrawingRecord } from './DrawCharting';

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  onOpen?: (chartId: string) => void;
}

// ─── Component ───

export function SavedDrawPreview({ patientId, encounterId, onOpen }: Props) {
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: chartRows = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qEncounterVisualCharts(encounterId, 'drawing'),
  );
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );
  const { data: templateRows = [] } = usePowerSyncQuery<ChartingTemplateRow>(
    qChartingTemplates(orgId),
  );

  const byId = useMemo(() => {
    const m = new Map<string, PatientFileRow>();
    for (const f of fileRows) m.set(f.id, f);
    return m;
  }, [fileRows]);

  const templatesById = useMemo(() => {
    const m = new Map<string, ChartingTemplateRow>();
    for (const t of templateRows) m.set(t.id, t);
    return m;
  }, [templateRows]);

  const charts = chartRows.filter((c) => c.drawing_data);

  if (charts.length === 0) return null;

  return (
    <>
      {charts.map((chart) => {
        let record: DrawingRecord | null = null;
        try {
          record = chart.drawing_data ? JSON.parse(chart.drawing_data) : null;
        } catch {
          // malformed — skip
        }
        if (!record) return null;

        // Resolve base image URL for image-mode or template-mode drawings
        let baseImageUrl: string | null = null;
        if (record.baseType === 'image' && record.baseImageId) {
          baseImageUrl = byId.get(record.baseImageId)?.url ?? null;
        } else if (record.baseType === 'template' && record.templateId) {
          baseImageUrl = templatesById.get(record.templateId)?.image_url ?? null;
        }

        return (
          <DrawCard
            key={chart.id}
            record={record}
            baseImageUrl={baseImageUrl}
            baseType={record.baseType}
            onClick={onOpen ? () => onOpen(chart.id) : undefined}
          />
        );
      })}
    </>
  );
}

// ─── Individual draw card with client-side Konva thumbnail ───

function DrawCard({
  record,
  baseImageUrl,
  baseType,
  onClick,
}: {
  record: DrawingRecord;
  baseImageUrl: string | null;
  baseType: string;
  onClick?: () => void;
}) {
  // For image-based drawings we render two layers:
  //   1. the Firebase image as a regular <img> (no CORS needed)
  //   2. the strokes as a transparent Konva overlay
  // For drawings with a Firebase base image (image-mode or template-mode),
  // use the layered approach: <img> + transparent strokes overlay.
  const isFirebaseImage =
    (baseType === 'image' || baseType === 'template') &&
    !!baseImageUrl &&
    baseImageUrl.includes('firebasestorage.googleapis.com');

  const [strokesUrl, setStrokesUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const generatedForRef = useRef<string | null>(null);

  useEffect(() => {
    // Build a cache key so we regenerate when data actually changes
    const key = `${baseType}:${baseImageUrl ?? ''}:${record.strokes.length}`;
    if (generatedForRef.current === key) return;
    generatedForRef.current = key;

    if (isFirebaseImage) {
      setThumbnailUrl(null);
      generateStrokesOverlay(record)
        .then(setStrokesUrl)
        .catch(() => {});
    } else {
      setStrokesUrl(null);
      generateThumbnail(record, baseImageUrl)
        .then(setThumbnailUrl)
        .catch(() => {});
    }
  }, [record, baseImageUrl, baseType, isFirebaseImage]);

  const label =
    baseType === 'image'
      ? 'Drawing (Image)'
      : baseType === 'template'
        ? 'Drawing (Template)'
        : 'Drawing';

  const ready = isFirebaseImage ? !!strokesUrl : !!thumbnailUrl;

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      title="Open Drawing"
      style={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: '#fff',
        cursor: onClick ? 'pointer' : 'default',
        padding: 0,
        width: '100%',
      }}
    >
      {ready ? (
        isFirebaseImage ? (
          /* Layered: base image + transparent strokes overlay */
          <>
            <img
              src={baseImageUrl!}
              alt="Base"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
            <img
              src={strokesUrl!}
              alt="Drawing strokes"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </>
        ) : (
          <img
            src={thumbnailUrl!}
            alt="Drawing preview"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        )
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Loading…
        </div>
      )}
      {/* "Drawing" badge — distinguishes from raw images (PRD.169 §6.4) */}
      <span
        style={{
          position: 'absolute',
          bottom: 4,
          left: 4,
          padding: '1px 6px',
          borderRadius: 'var(--radius-sm)',
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          zIndex: 3,
        }}
      >
        {label}
      </span>
    </button>
  );
}

// ─── Offscreen Konva thumbnail generation ───

const THUMB_SIZE = 240;

/**
 * Generate a transparent PNG with only the drawing strokes (no background).
 * Used as an overlay on top of a regular <img> for Firebase image-based drawings.
 *
 * `targetSize` is the longest-edge pixel size of the output. Defaults to the
 * card thumbnail size; the WhatsApp share path passes a larger value so the
 * strokes stay crisp when layered into the full-size comparison card.
 */
export async function generateStrokesOverlay(
  record: DrawingRecord,
  targetSize: number = THUMB_SIZE,
): Promise<string> {
  const { canvasWidth, canvasHeight, strokes } = record;
  const w = canvasWidth || 600;
  const h = canvasHeight || 450;

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  try {
    const stage = new Konva.Stage({ container, width: w, height: h });
    const layer = new Konva.Layer();
    stage.add(layer);

    for (const stroke of strokes) {
      layer.add(
        new Konva.Line({
          points: stroke.points,
          stroke: stroke.tool === 'eraser' ? '#ffffff' : stroke.color,
          strokeWidth: stroke.tool === 'eraser' ? stroke.strokeWidth * 3 : stroke.strokeWidth,
          tension: 0.5,
          lineCap: 'round',
          lineJoin: 'round',
          globalCompositeOperation: stroke.tool === 'eraser' ? 'destination-out' : 'source-over',
        }),
      );
    }

    layer.draw();

    const dataUrl = stage.toDataURL({
      pixelRatio: targetSize / Math.max(w, h),
    });

    stage.destroy();
    return dataUrl;
  } finally {
    document.body.removeChild(container);
  }
}

export async function generateThumbnail(
  record: DrawingRecord,
  baseImageUrl: string | null,
): Promise<string> {
  const { canvasWidth, canvasHeight, strokes } = record;
  const w = canvasWidth || 600;
  const h = canvasHeight || 450;

  // Create offscreen container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  try {
    const stage = new Konva.Stage({ container, width: w, height: h });

    // Base layer
    const baseLayer = new Konva.Layer();
    stage.add(baseLayer);

    // White background
    baseLayer.add(new Konva.Rect({ x: 0, y: 0, width: w, height: h, fill: '#ffffff' }));

    // Base image (if applicable).
    // Skip Firebase Storage URLs — they don't serve CORS headers for localhost,
    // and canvas toDataURL() requires CORS. Strokes render on white instead.
    if (baseImageUrl && !baseImageUrl.includes('firebasestorage.googleapis.com')) {
      const img = await loadImage(baseImageUrl);
      const { x, y, fw, fh } = containFit(img.naturalWidth, img.naturalHeight, w, h);
      baseLayer.add(new Konva.Image({ image: img, x, y, width: fw, height: fh }));
    } else if (record.baseType === 'template' && record.templateId) {
      const tplUrl = getTemplateUrl(record.templateId);
      if (tplUrl) {
        const img = await loadImage(tplUrl);
        const { x, y, fw, fh } = containFit(img.naturalWidth, img.naturalHeight, w, h);
        baseLayer.add(new Konva.Image({ image: img, x, y, width: fw, height: fh }));
      }
    }

    baseLayer.draw();

    // Drawing layer
    const drawLayer = new Konva.Layer();
    stage.add(drawLayer);

    for (const stroke of strokes) {
      drawLayer.add(
        new Konva.Line({
          points: stroke.points,
          stroke: stroke.tool === 'eraser' ? '#ffffff' : stroke.color,
          strokeWidth: stroke.tool === 'eraser' ? stroke.strokeWidth * 3 : stroke.strokeWidth,
          tension: 0.5,
          lineCap: 'round',
          lineJoin: 'round',
          globalCompositeOperation: stroke.tool === 'eraser' ? 'destination-out' : 'source-over',
        }),
      );
    }

    drawLayer.draw();

    // Export as data URL at thumbnail resolution
    const dataUrl = stage.toDataURL({
      pixelRatio: THUMB_SIZE / Math.max(w, h),
    });

    stage.destroy();
    return dataUrl;
  } finally {
    document.body.removeChild(container);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Template URL lookup — mirrors TEMPLATES from DrawCharting.
// Kept minimal; avoids importing the full array to reduce coupling.
function getTemplateUrl(templateId: string): string | null {
  const TEMPLATE_SVGS: Record<string, string> = {
    'face-front': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 500" width="400" height="500"><rect width="400" height="500" fill="white"/><ellipse cx="200" cy="220" rx="130" ry="170" fill="none" stroke="#ddd" stroke-width="1.5"/><circle cx="155" cy="190" r="18" fill="none" stroke="#ddd" stroke-width="1"/><circle cx="245" cy="190" r="18" fill="none" stroke="#ddd" stroke-width="1"/><path d="M180 260 Q200 285 220 260" fill="none" stroke="#ddd" stroke-width="1"/><path d="M165 310 Q200 335 235 310" fill="none" stroke="#ddd" stroke-width="1"/><line x1="200" y1="240" x2="200" y2="275" stroke="#ddd" stroke-width="0.5"/></svg>`,
    'face-profile': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 500" width="400" height="500"><rect width="400" height="500" fill="white"/><path d="M220 60 Q290 80 300 160 Q310 200 290 240 L280 260 Q300 290 280 320 Q260 360 240 380 Q230 390 210 400 Q190 390 180 370 Q170 340 180 300 Q150 280 140 240 Q120 200 130 150 Q140 100 180 70 Z" fill="none" stroke="#ddd" stroke-width="1.5"/><circle cx="260" cy="185" r="14" fill="none" stroke="#ddd" stroke-width="1"/><path d="M275 250 Q285 260 275 270" fill="none" stroke="#ddd" stroke-width="1"/><path d="M240 310 Q260 325 240 335" fill="none" stroke="#ddd" stroke-width="1"/></svg>`,
    'body-front': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 600" width="400" height="600"><rect width="400" height="600" fill="white"/><circle cx="200" cy="50" r="30" fill="none" stroke="#ddd" stroke-width="1.5"/><line x1="200" y1="80" x2="200" y2="260" stroke="#ddd" stroke-width="1.5"/><line x1="200" y1="120" x2="130" y2="210" stroke="#ddd" stroke-width="1.5"/><line x1="200" y1="120" x2="270" y2="210" stroke="#ddd" stroke-width="1.5"/><line x1="200" y1="260" x2="160" y2="420" stroke="#ddd" stroke-width="1.5"/><line x1="200" y1="260" x2="240" y2="420" stroke="#ddd" stroke-width="1.5"/><line x1="160" y1="420" x2="150" y2="560" stroke="#ddd" stroke-width="1.5"/><line x1="240" y1="420" x2="250" y2="560" stroke="#ddd" stroke-width="1.5"/></svg>`,
  };
  const svg = TEMPLATE_SVGS[templateId];
  return svg ? 'data:image/svg+xml,' + encodeURIComponent(svg) : null;
}

/** Compute "object-fit: contain" dimensions for an image within a box. */
function containFit(imgW: number, imgH: number, boxW: number, boxH: number) {
  const scale = Math.min(boxW / imgW, boxH / imgH);
  const fw = imgW * scale;
  const fh = imgH * scale;
  return { x: (boxW - fw) / 2, y: (boxH - fh) / 2, fw, fh };
}
